import { useEffect, useState, type FormEvent, type ReactNode } from 'react'
import { useAppStore } from '../stores/app-store'

type AuthMode = 'signin' | 'create'

type SessionUser = {
  id: string
  email: string
}

type SessionResponse =
  | {
      authenticated: true
      user: SessionUser
    }
  | {
      authenticated: false
      bootstrapRequired: boolean
    }

async function readJson<T>(res: Response): Promise<T | null> {
  try {
    return await res.json() as T
  } catch {
    return null
  }
}

async function readErrorMessage(res: Response, fallback: string): Promise<string> {
  const payload = await readJson<{ message?: string; error?: string }>(res)
  return payload?.message || payload?.error || fallback
}

export function LoginGate({ children }: { children: ReactNode }) {
  const authenticated = useAppStore((state) => state.authenticated)
  const setAuthenticated = useAppStore((state) => state.setAuthenticated)
  const [mode, setMode] = useState<AuthMode>('signin')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [checking, setChecking] = useState(true)
  const [loading, setLoading] = useState(false)
  const [bootstrapRequired, setBootstrapRequired] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    let cancelled = false

    const checkSession = async () => {
      try {
        const res = await fetch('/api/auth/session', { credentials: 'include' })
        const data = await readJson<SessionResponse>(res)

        if (cancelled) return

        if (res.ok && data?.authenticated) {
          setAuthenticated(true)
          setBootstrapRequired(false)
          return
        }

        const shouldBootstrap = res.status === 401 && data?.authenticated === false && Boolean(data.bootstrapRequired)
        setAuthenticated(false)
        setBootstrapRequired(shouldBootstrap)
        setMode(shouldBootstrap ? 'create' : 'signin')
      } catch {
        if (!cancelled) {
          setAuthenticated(false)
          setBootstrapRequired(false)
          setMode('signin')
        }
      } finally {
        if (!cancelled) {
          setChecking(false)
        }
      }
    }

    void checkSession()

    return () => {
      cancelled = true
    }
  }, [setAuthenticated])

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setError('')

    if (!email.trim() || !password) {
      setError('Email and password are required.')
      return
    }

    setLoading(true)

    try {
      const endpoint = mode === 'create' ? '/api/auth/register' : '/api/auth/login'
      const res = await fetch(endpoint, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: email.trim(),
          password
        })
      })

      if (!res.ok) {
        throw new Error(await readErrorMessage(res, mode === 'create' ? 'Unable to create account.' : 'Unable to sign in.'))
      }

      const sessionRes = await fetch('/api/auth/session', { credentials: 'include' })
      const session = await readJson<SessionResponse>(sessionRes)

      if (sessionRes.ok && session?.authenticated) {
        setAuthenticated(true)
        setBootstrapRequired(false)
        return
      }

      throw new Error('Session could not be established.')
    } catch (err) {
      setPassword('')
      setError(err instanceof Error ? err.message : 'Authentication failed.')
    } finally {
      setLoading(false)
    }
  }

  if (checking) {
    return (
      <div className="login-gate">
        <div className="login-box auth-box">
          <span className="login-logo">ENCLOSED RALPH</span>
          <span className="login-sub">authenticating...</span>
        </div>
      </div>
    )
  }

  if (authenticated) {
    return <>{children}</>
  }

  return (
    <div className="login-gate">
      <form className="login-box auth-box" onSubmit={handleSubmit}>
        <div className="auth-box-header">
          <span className="login-logo">ENCLOSED RALPH</span>
          <span className="login-sub">mission control</span>
        </div>

        <div className="auth-tabs" role="tablist" aria-label="Authentication mode">
          <button
            type="button"
            className={`auth-tab ${mode === 'signin' ? 'is-active' : ''}`}
            onClick={() => setMode('signin')}
            disabled={bootstrapRequired}
          >
            Sign in
          </button>
          <button
            type="button"
            className={`auth-tab ${mode === 'create' ? 'is-active' : ''}`}
            onClick={() => setMode('create')}
          >
            Create account
          </button>
        </div>

        {bootstrapRequired && (
          <div className="auth-note">
            Bootstrap required. Create the first workspace account to unlock the control surface.
          </div>
        )}

        <div className="auth-fields">
          <label className="auth-field">
            <span>Email</span>
            <input
              type="email"
              className="config-input login-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="operator@company.com"
              autoComplete="email"
              autoFocus
            />
          </label>

          <label className="auth-field">
            <span>Password</span>
            <input
              type="password"
              className="config-input login-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={mode === 'create' ? 'create a password' : 'your password'}
              autoComplete={mode === 'create' ? 'new-password' : 'current-password'}
            />
          </label>
        </div>

        {error && <span className="login-error">{error}</span>}

        <button type="submit" className="btn btn-primary login-btn" disabled={loading}>
          {loading ? 'Working...' : (mode === 'create' ? 'Create account' : 'Sign in')}
        </button>
      </form>
    </div>
  )
}
